import React from 'react';
import { Avatar, Tooltip } from 'antd';
import {
  DownloadOutlined,
  UserOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import type { KanbanItem as KanbanItemType } from '@/api/types/kanban.types';

interface KanbanItemProps {
  item: KanbanItemType;
}

/**
 * Kanban Item (Ticket) Component
 * Phase 4: Draggable ticket with complete details
 */
export const KanbanItem: React.FC<KanbanItemProps> = ({ item }) => {
  const { formatDate } = useUserTimezone();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `item-${item.id}`,
    data: {
      type: 'item',
      item,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Format date in user's timezone (e.g., "17.11")
  const displayDate = formatDate(item.created_at, 'DD.MM');
  const ticketId = item.item_data.unique_id;

  // TODO: Get attachments from API when available
  // For now, using mock data based on screenshot
  const hasAttachments = item.tags && item.tags.length > 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`kanban-item ${isDragging ? 'kanban-item-dragging' : ''}`}
      {...attributes}
      {...listeners}
    >
      {/* Ticket Title */}
      <div className="kanban-item-title">{item.title}</div>

      {/* Attachments Section */}
      {hasAttachments && (
        <div className="kanban-item-attachments">
          {item.tags.slice(0, 2).map((tag, index) => (
            <div key={index} className="kanban-item-attachment">
              <PaperClipOutlined className="attachment-icon" />
              <span className="attachment-name">{tag}</span>
              <DownloadOutlined className="attachment-download" />
            </div>
          ))}
        </div>
      )}

      {/* Footer: Ticket ID and Avatar */}
      <div className="kanban-item-footer">
        <div className="kanban-item-id">
          {displayDate} <span className="ticket-id">{ticketId}</span>
        </div>
        <div className="kanban-item-avatar">
          {item.assigned_to_id ? (
            <Tooltip title={`Assigned to: ${item.assigned_to_id}`}>
              <Avatar size={28} icon={<UserOutlined />} />
            </Tooltip>
          ) : (
            <Avatar
              size={28}
              icon={<UserOutlined />}
              style={{ opacity: 0.5 }}
            />
          )}
        </div>
      </div>
    </div>
  );
};
